import Row from '$tablet-app/src/app/components/Row';
import Spacer from '$tablet-app/src/app/components/Spacer';
import Text from '$tablet-app/src/app/components/Text';
import GoalCompletionCircle from '$tablet-app/src/app/goals/components/GoalCompletionCircle';
import { getGoalProgressPercentage } from '$tablet-app/src/app/goals/functions';
import { useMyGoals } from '$tablet-app/src/app/goals/useGoals/useGoals';
import { palette } from '$tablet-app/src/app/theme';
import useCurrentDay from '$tablet-app/src/app/utils/useCurrentDay';
import { FontAwesomeIcon } from '@fortawesome/react-native-fontawesome';
import React from 'react';
import { TouchableWithoutFeedback } from 'react-native-gesture-handler';
import styled from 'styled-components/native';
import { useIncrementGoal } from '../../goals/hooks/useIncrementGoal';
import { useMainDrawerNavigaton } from '../../navigation/hooks';
import { useCurrentUserID } from '../../users';
import DashboardPanel from '../DashboardPanel';
import DashboardPanelTitle from '../DashboardPanelTitle';

const GoalDetails = () => {
  const today = useCurrentDay();
  const user_id = useCurrentUserID();

  // Get a new random motivational quote daily
  const getQuote = () => {
    // Generate unique random number based on the user id and the current date string
    const dailyKey = user_id.trim() + new Date().toLocaleDateString();
    const seedrandom = require('seedrandom');
    const rng = seedrandom(dailyKey);

    // Retrieve random quote from the json list
    const jsonData = require('../../sticky-notes/quotes.json');
    const idx = Math.floor(rng() * jsonData.length);
    return jsonData[idx];
  };

  const { data: goals } = useMyGoals();

  const navigation = useMainDrawerNavigaton();

  const [goalIndex, setGoalIndex] = React.useState(0);

  const goalKey = {
    date: new Date(today),
    user_id,
  };

  const increment = useIncrementGoal(goalKey);

  const goal = goals[goalIndex];
  const showNavs = goals.length > 1;

  const onClickAddGoals = () => navigation.navigate('Goals');

  const onPress = () => {
    if (!goal) return;
    increment(goal);
  };

  const onSwitchGoals = (direction: 1 | -1) => () => {
    const newIndex = goalIndex + direction;
    setGoalIndex(newIndex < 0 ? goals.length - 1 : newIndex % goals.length);
  };

  return (
    <Container>
      {goal ? (
        <Row height="100%">
          <Column>
            <DashboardPanelTitle text="TODAY'S GOALS" />

            <Row flex={1}>
              {showNavs ? (
                <NavSideBar onPress={onSwitchGoals(-1)}>
                  <FontAwesomeIcon icon="chevron-left" />
                </NavSideBar>
              ) : null}

              <GoalCircleContainer>
                <TouchableWithoutFeedback
                  onPress={onPress}
                  delayLongPress={200}
                >
                  <Spacer height="16px" />
                  <GoalCompletionCircle
                    goal={goal}
                    progress={getGoalProgressPercentage(goal, goalKey)}
                  />

                  <Row justifyContent="center" marginTop={8}>
                    <Text weight="semibold">{goal.title}</Text>
                  </Row>
                </TouchableWithoutFeedback>
              </GoalCircleContainer>

              {showNavs ? (
                <NavSideBar onPress={onSwitchGoals(1)}>
                  <FontAwesomeIcon icon="chevron-right" />
                </NavSideBar>
              ) : null}
            </Row>
          </Column>

          <Column>
            <QuoteScroll>
              <Card>
                <CardText variant="stickyNote">{getQuote().quote}</CardText>
                <CardText variant="stickyNote2">{getQuote().author}</CardText>
              </Card>
            </QuoteScroll>
          </Column>
        </Row>
      ) : (
        <PlaceholderArea onPress={onClickAddGoals}>
          <Text variant="h3">
            You have no goals set up. Click here to add some.
          </Text>
        </PlaceholderArea>
      )}
    </Container>
  );
};

const GoalCircleContainer = styled.View`
  justify-content: center;
  flex: 1;
`;

const Container = styled(DashboardPanel)`
  flex: 1;
`;

const Column = styled.View`
  flex: 1;
  padding: 16px;
`;

const Card = styled.View`
  justify-content: center;
  align-items: center;
  text-align: center;
`;

const QuoteScroll = styled.ScrollView`
  padding: 8px 10px;
  background-color: ${palette.pastel_green};
  border-radius: 10px;
  flex: 1;
`;

const CardText = styled(Text)`
  text-align: center;
  padding: 8px;
`;

const PlaceholderArea = styled.TouchableOpacity`
  height: 100%;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

const NavSideBar = styled.TouchableOpacity`
  flex-grow: 0;
  justify-content: center;
  padding: 0 8px;
`;

export default GoalDetails;
